import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { format } from 'date-fns';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useDeleteInvoiceMutation from 'src/services/edi_admin_tools/useDeleteInvoiceMutation';
import useExportPOTimingLogsApi from 'src/services/edi_admin_tools/useExportPOTimingLogsApi';
import useOutstandingInvoiceLogsQuery from 'src/services/edi_admin_tools/useOutstandingInvoiceLogsQuery';
import usePOTimingLogsQuery from 'src/services/edi_admin_tools/usePOTimingLogsQuery';
import useResendPOsMutation from 'src/services/edi_admin_tools/useResendPOsMutation';
import useRollBackPOsMutation from 'src/services/edi_admin_tools/useRollBackPOsMutation';
import {
  DeleteInvoiceRequestItem,
  Outstanding810Log,
  OutstandingInvoiceLogItem,
  POTimingLogItem,
  ResendPORequestItem,
  RollBackPORequestItem,
  TimingLog,
} from 'src/types/edi';
import exportToExcel from 'src/utils/exportToExcel';
import { downloadFileFromUrl } from 'src/utils/fileTransformation';
import { createContext, useContext } from 'use-context-selector';

const TIMING_LOGS_VIEW = 0;
const OUTSTANDING_810_VIEW = 1;

function getEdiLogsExportFileName(selectedView: number): string {
  const dated = format(new Date(), 'yyyy-MM-dd');
  return selectedView === TIMING_LOGS_VIEW
    ? `Timing_logs_${dated}.xls`
    : `Outstanding_810_logs_${dated}.xls`;
}

export interface LogSelectionState {
  selected: boolean;
}

export type TimingLogSelections = Record<string, LogSelectionState>;
export type OutstandingLogSelections = Record<string, LogSelectionState>;

export type TimingLogGridRow = TimingLog & { selected: boolean };
export type Outstanding810GridRow = Outstanding810Log & { selected: boolean };

const initialTimingLogsDataState: State = {
  skip: 0,
  take: 100,
  sort: [],
};

const initialOutstanding810DataState: State = {
  skip: 0,
  take: 100,
  sort: [],
};

function toDateOnly(dateString: string): Date {
  const d = new Date(dateString);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function stripLeadingBracketedDocumentValue(value?: string | null): string {
  if (!value) {
    return '';
  }

  return value
    .replace(/^\(<b>.*?<\/b>\)\s*/i, '')
    .replace(/^\([^)]*\)\s*/, '')
    .trim();
}

function mapOutstandingInvoiceLogItemToRow(
  item: OutstandingInvoiceLogItem,
  selected: boolean
): Outstanding810GridRow {
  return {
    documentId: item.documentId,
    poNum: item.poNumber ?? '',
    dateReceived: toDateOnly(item.dateSent),
    dateReceivedRaw: item.dateSent,
    supplier: item.supplierCompanyName,
    fileName: item.originalFileName,
    supplierCustomerNumber: item.supplierCustomerNumber,
    document: stripLeadingBracketedDocumentValue(item.document),
    selected,
  };
}

function mapPOTimingLogItemToRow(item: POTimingLogItem, selected: boolean): TimingLogGridRow {
  return {
    documentId: item.documentId,
    poNum: item.poNumber,
    dateSent: toDateOnly(item.ftpDate),
    dateSentRaw: item.ftpDate,
    buyer: item.buyer,
    supplier: item.supplierCompanyName,
    distributor: item.distributor,
    customerNumber: item.customerNumber,
    fileName: item.fileName,
    selected,
  };
}

interface EdiAdminContextType {
  selectedView: number;
  setSelectedView: (view: number) => void;
  timingLogsDataState: State;
  onTimingLogsDataStateChange: (event: GridDataStateChangeEvent) => void;
  timingLogsGridData: TimingLogGridRow[];
  timingLogsTotal: number;
  timingLogsLoading: boolean;
  timingLogsError: Error | null;
  timingLogsDataUpdatedAt: number;
  timingLogsRequestedAt: number;
  refetchTimingLogs: () => void;
  onTimingLogsHeaderSelectionChange: (event: GridHeaderSelectionChangeEvent) => void;
  timingLogSelections: TimingLogSelections;
  setTimingLogSelections: React.Dispatch<React.SetStateAction<TimingLogSelections>>;
  outstandingLogSelections: OutstandingLogSelections;
  setOutstandingLogSelections: React.Dispatch<React.SetStateAction<OutstandingLogSelections>>;
  onTimingSelectionChange: (event: GridSelectionChangeEvent) => void;
  onOutstandingSelectionChange: (event: GridSelectionChangeEvent) => void;
  hasSelectedTimingLogs: boolean;
  hasSelectedOutstandingLogs: boolean;
  rollBackSelectedTimingLogs: () => Promise<void>;
  isRollBackTimingLogsPending: boolean;
  resendSelectedTimingLogs: () => Promise<void>;
  isResendTimingLogsPending: boolean;
  outstanding810GridData: Outstanding810GridRow[];
  outstanding810Loading: boolean;
  outstanding810DataUpdatedAt: number;
  outstanding810RequestedAt: number;
  refetchOutstandingInvoiceLogs: () => void;
  onOutstanding810HeaderSelectionChange: (event: GridHeaderSelectionChangeEvent) => void;
  outstanding810GridDataState: State;
  setOutstanding810GridDataState: React.Dispatch<React.SetStateAction<State>>;
  deleteSelectedOutstanding810Logs: () => Promise<void>;
  isDeleteOutstanding810sPending: boolean;
  exportCurrentViewLogs: () => Promise<void>;
  isExportLogsPending: boolean;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

export const EdiAdminContext = createContext<EdiAdminContextType>({} as EdiAdminContextType);

interface EdiAdminContextProviderProps {
  children: ReactNode;
}

export const EdiAdminContextProvider: FC<EdiAdminContextProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [selectedView, setSelectedViewState] = useState(0);
  const [timingLogsDataState, setTimingLogsDataState] = useState<State>(initialTimingLogsDataState);
  const [outstanding810GridDataState, setOutstanding810GridDataState] = useState<State>(
    initialOutstanding810DataState
  );
  const [timingLogSelections, setTimingLogSelections] = useState<TimingLogSelections>({});
  const [outstandingLogSelections, setOutstandingLogSelections] =
    useState<OutstandingLogSelections>({});
  const timingLogItemsByDocumentIdRef = useRef<Map<number, POTimingLogItem>>(new Map());
  const outstandingInvoiceLogItemsByDocumentIdRef = useRef<Map<number, OutstandingInvoiceLogItem>>(
    new Map()
  );

  const rollBackPOsMutation = useRollBackPOsMutation();
  const resendPOsMutation = useResendPOsMutation();
  const deleteInvoiceMutation = useDeleteInvoiceMutation();

  const { exportExecute: exportPOTimingLogsExecute, exportLoading: exportPOTimingLogsLoading } =
    useExportPOTimingLogsApi();

  const {
    data: outstandingInvoiceLogsData,
    isFetching: outstanding810Loading,
    dataUpdatedAt: outstanding810DataUpdatedAt,
    lastRequestedAt: outstanding810RequestedAt,
    refetch: refetchOutstandingInvoiceLogs,
  } = useOutstandingInvoiceLogsQuery({
    enabled: selectedView === OUTSTANDING_810_VIEW,
  });

  useEffect(() => {
    outstandingInvoiceLogsData?.forEach((item) => {
      outstandingInvoiceLogItemsByDocumentIdRef.current.set(item.documentId, item);
    });
  }, [outstandingInvoiceLogsData]);

  const setSelectedView = useCallback((view: number) => setSelectedViewState(view), []);

  const hasAnyFilter = useCallback((filter: State['filter']) => {
    if (!filter?.filters?.length) return false;
    return filter.filters.some((item: CompositeFilterDescriptor | FilterDescriptor) =>
      'filters' in item ? item.filters.length > 0 : true
    );
  }, []);

  const onTimingLogsDataStateChange = useCallback((event: GridDataStateChangeEvent) => {
    setTimingLogsDataState(event.dataState);
  }, []);

  const {
    data: timingLogsResponse,
    isFetching: timingLogsLoading,
    error: timingLogsQueryError,
    dataUpdatedAt: timingLogsDataUpdatedAt,
    lastRequestedAt: timingLogsRequestedAt,
    refetch: refetchTimingLogs,
  } = usePOTimingLogsQuery({ enabled: selectedView === TIMING_LOGS_VIEW });

  useEffect(() => {
    timingLogsResponse?.items?.forEach((item) => {
      timingLogItemsByDocumentIdRef.current.set(item.documentId, item);
    });
  }, [timingLogsResponse?.items]);

  const outstanding810GridData = useMemo((): Outstanding810GridRow[] => {
    const items = outstandingInvoiceLogsData;
    if (!items?.length) return [];
    return items.map((item) =>
      mapOutstandingInvoiceLogItemToRow(
        item,
        outstandingLogSelections[item.documentId]?.selected ?? false
      )
    );
  }, [outstandingInvoiceLogsData, outstandingLogSelections]);

  const onOutstanding810HeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement = event?.syntheticEvent?.target as HTMLInputElement | undefined;
      const checked = checkboxElement?.checked ?? false;
      setOutstandingLogSelections((prev) => {
        const updated = { ...prev };
        outstanding810GridData.forEach((item) => {
          updated[item.documentId] = { selected: checked };
        });
        return updated;
      });
    },
    [outstanding810GridData, setOutstandingLogSelections]
  );

  const timingLogsGridData = useMemo((): TimingLogGridRow[] => {
    const items = timingLogsResponse?.items;
    if (!items?.length) return [];
    return items.map((item) =>
      mapPOTimingLogItemToRow(item, timingLogSelections[item.documentId]?.selected ?? false)
    );
  }, [timingLogsResponse?.items, timingLogSelections]);

  const timingLogsTotal = timingLogsGridData.length;

  const onTimingLogsHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement = event?.syntheticEvent?.target as HTMLInputElement | undefined;
      const checked = checkboxElement?.checked ?? false;
      setTimingLogSelections((prev) => {
        const updated = { ...prev };
        timingLogsGridData.forEach((item) => {
          updated[item.documentId] = { selected: checked };
        });
        return updated;
      });
    },
    [timingLogsGridData, setTimingLogSelections]
  );

  const onTimingSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
    const documentId = event?.dataItem?.documentId;
    if (documentId == null) return;
    setTimingLogSelections((prev) => {
      const current = prev[documentId] ?? { selected: false };
      return {
        ...prev,
        [documentId]: { selected: !current.selected },
      };
    });
  }, []);

  const onOutstandingSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
    const documentId = event?.dataItem?.documentId;
    if (documentId == null) return;
    setOutstandingLogSelections((prev) => {
      const current = prev[documentId] ?? { selected: false };
      return {
        ...prev,
        [documentId]: { selected: !current.selected },
      };
    });
  }, []);

  const hasSelectedTimingLogs = useMemo(
    () => Object.values(timingLogSelections).some((s) => s.selected),
    [timingLogSelections]
  );

  const hasActiveFilters = useMemo(() => {
    const activeDataState =
      selectedView === TIMING_LOGS_VIEW ? timingLogsDataState : outstanding810GridDataState;
    const hasFilter = hasAnyFilter(activeDataState.filter);
    const hasSort = (activeDataState.sort?.length ?? 0) > 0;
    return hasFilter || hasSort;
  }, [hasAnyFilter, outstanding810GridDataState, selectedView, timingLogsDataState]);

  const clearFilters = useCallback(() => {
    if (selectedView === TIMING_LOGS_VIEW) {
      setTimingLogsDataState((prev) => ({
        ...prev,
        sort: [],
        filter: undefined,
      }));
      return;
    }

    setOutstanding810GridDataState((prev) => ({
      ...prev,
      sort: [],
      filter: undefined,
    }));
  }, [selectedView]);

  const hasSelectedOutstandingLogs = useMemo(
    () => Object.values(outstandingLogSelections).some((s) => s.selected),
    [outstandingLogSelections]
  );

  const rollBackSelectedTimingLogs = useCallback(async () => {
    const payload: RollBackPORequestItem[] = [];
    for (const [key, sel] of Object.entries(timingLogSelections)) {
      if (!sel.selected) continue;
      const docId = Number(key);
      const item = timingLogItemsByDocumentIdRef.current.get(docId);
      if (item) {
        payload.push({
          documentId: item.documentId,
          poId: item.poId,
          buyerId: item.buyerId,
          supplierId: item.supplierId,
        });
      }
    }
    if (payload.length === 0) return;
    try {
      await rollBackPOsMutation.mutateAsync(payload);
      toast.success(t('ediRollBackPosSuccess'));
      await refetchTimingLogs();
      setTimingLogSelections((prev) => {
        const next = { ...prev };
        payload.forEach((p) => {
          delete next[p.documentId];
        });
        return next;
      });
    } catch {
      // Errors are surfaced by the axios response interceptor
    }
  }, [timingLogSelections, rollBackPOsMutation, refetchTimingLogs, t]);

  const resendSelectedTimingLogs = useCallback(async () => {
    const payload: ResendPORequestItem[] = [];
    for (const [key, sel] of Object.entries(timingLogSelections)) {
      if (!sel.selected) continue;
      payload.push({ documentId: Number(key) });
    }
    if (payload.length === 0) return;
    try {
      await resendPOsMutation.mutateAsync(payload);
      toast.success(t('ediResendPosSuccess'));
      await refetchTimingLogs();
      setTimingLogSelections((prev) => {
        const next = { ...prev };
        payload.forEach((item) => {
          delete next[item.documentId];
        });
        return next;
      });
    } catch {
      // Errors are surfaced by the axios response interceptor
    }
  }, [timingLogSelections, resendPOsMutation, refetchTimingLogs, t]);

  const deleteSelectedOutstanding810Logs = useCallback(async () => {
    const payload: DeleteInvoiceRequestItem[] = [];
    for (const [key, sel] of Object.entries(outstandingLogSelections)) {
      if (!sel.selected) continue;
      const docId = Number(key);
      const item = outstandingInvoiceLogItemsByDocumentIdRef.current.get(docId);
      if (item) {
        payload.push({
          documentId: item.documentId,
          poNumber: item.poNumber ?? '',
        });
      }
    }
    if (payload.length === 0) return;
    try {
      await deleteInvoiceMutation.mutateAsync(payload);
      toast.success(t('ediDeleteOutstanding810sSuccess'));
      await refetchOutstandingInvoiceLogs();
      setOutstandingLogSelections((prev) => {
        const next = { ...prev };
        payload.forEach((p) => {
          delete next[p.documentId];
        });
        return next;
      });
    } catch {
      // Errors are surfaced by the axios response interceptor
    }
  }, [outstandingLogSelections, deleteInvoiceMutation, refetchOutstandingInvoiceLogs, t]);

  const exportCurrentViewLogs = useCallback(async () => {
    try {
      if (selectedView === OUTSTANDING_810_VIEW) {
        const columnMapping: Record<string, string> = {
          poNum: t('PO #'),
          dateReceived: t('Date Received'),
          supplier: t('Supplier'),
          fileName: t('File Name'),
          documentId: t('Document ID'),
          supplierCustomerNumber: t('Supplier Customer #'),
          document: t('Document'),
        };
        const columns = Object.keys(columnMapping);
        const rows = outstanding810GridData.map((row) => ({
          poNum: row.poNum,
          dateReceived: format(new Date(row.dateReceivedRaw), 'MM/dd/yyyy hh:mm a'),
          supplier: row.supplier,
          fileName: row.fileName,
          documentId: row.documentId,
          supplierCustomerNumber: row.supplierCustomerNumber,
          document: row.document,
        }));

        exportToExcel({
          columns,
          rows,
          columnMapping,
          fileName: getEdiLogsExportFileName(selectedView).replace(/\.xls$/i, ''),
        });
        toast.success(t('ediExportLogsSuccess'));
        return;
      }

      const res = await exportPOTimingLogsExecute();
      if (!res?.data?.downloadUrl) {
        return;
      }
      await downloadFileFromUrl(res.data.downloadUrl, getEdiLogsExportFileName(selectedView));
      toast.success(t('ediExportLogsSuccess'));
    } catch {
      toast.error(t('ediExportLogsError'));
    }
  }, [exportPOTimingLogsExecute, outstanding810GridData, selectedView, t]);

  const isExportLogsPending = Boolean(exportPOTimingLogsLoading);

  const timingLogsError = (timingLogsQueryError as Error | null) ?? null;

  const value: EdiAdminContextType = useMemo(
    () => ({
      selectedView,
      setSelectedView,
      timingLogsDataState,
      onTimingLogsDataStateChange,
      timingLogsGridData,
      timingLogsTotal,
      timingLogsLoading,
      timingLogsError,
      timingLogsDataUpdatedAt,
      timingLogsRequestedAt,
      refetchTimingLogs,
      onTimingLogsHeaderSelectionChange,
      timingLogSelections,
      setTimingLogSelections,
      outstandingLogSelections,
      setOutstandingLogSelections,
      onTimingSelectionChange,
      onOutstandingSelectionChange,
      hasSelectedTimingLogs,
      hasSelectedOutstandingLogs,
      rollBackSelectedTimingLogs,
      isRollBackTimingLogsPending: rollBackPOsMutation.isPending,
      resendSelectedTimingLogs,
      isResendTimingLogsPending: resendPOsMutation.isPending,
      outstanding810GridData,
      outstanding810Loading,
      outstanding810DataUpdatedAt,
      outstanding810RequestedAt,
      refetchOutstandingInvoiceLogs,
      onOutstanding810HeaderSelectionChange,
      outstanding810GridDataState,
      setOutstanding810GridDataState,
      deleteSelectedOutstanding810Logs,
      isDeleteOutstanding810sPending: deleteInvoiceMutation.isPending,
      exportCurrentViewLogs,
      isExportLogsPending,
      clearFilters,
      hasActiveFilters,
    }),
    [
      selectedView,
      setSelectedView,
      timingLogsDataState,
      onTimingLogsDataStateChange,
      timingLogsGridData,
      timingLogsTotal,
      timingLogsLoading,
      timingLogsError,
      timingLogsDataUpdatedAt,
      timingLogsRequestedAt,
      refetchTimingLogs,
      onTimingLogsHeaderSelectionChange,
      timingLogSelections,
      outstandingLogSelections,
      onTimingSelectionChange,
      onOutstandingSelectionChange,
      hasSelectedTimingLogs,
      hasSelectedOutstandingLogs,
      rollBackSelectedTimingLogs,
      rollBackPOsMutation.isPending,
      resendSelectedTimingLogs,
      resendPOsMutation.isPending,
      outstanding810GridData,
      outstanding810Loading,
      outstanding810DataUpdatedAt,
      outstanding810RequestedAt,
      refetchOutstandingInvoiceLogs,
      onOutstanding810HeaderSelectionChange,
      outstanding810GridDataState,
      setOutstanding810GridDataState,
      deleteSelectedOutstanding810Logs,
      deleteInvoiceMutation.isPending,
      exportCurrentViewLogs,
      isExportLogsPending,
      clearFilters,
      hasActiveFilters,
    ]
  );

  return <EdiAdminContext.Provider value={value}>{children}</EdiAdminContext.Provider>;
};

export const useEdiAdminContext = () => {
  const context = useContext(EdiAdminContext);
  if (!context) {
    throw new Error('useEdiAdminContext must be used within EdiAdminContextProvider');
  }
  return context;
};
